/**
 * Simple Capacitor utilities
 */
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Check if app is running in a native Capacitor container
 */
export const isCapacitorAvailable = (): boolean => {
  try {
    return !!window.Capacitor?.isNativePlatform();
  } catch (e) {
    console.error('Error checking Capacitor platform:', e);
    return false;
  }
};

/**
 * Get the current platform (ios, android, web)
 */
export const getPlatform = (): string => {
  try {
    return window.Capacitor?.getPlatform() || 'web';
  } catch (e) {
    console.error('Error getting platform:', e);
    return 'web';
  }
};

/**
 * Initialize Capacitor plugins
 */
export const initializeCapacitor = async (): Promise<void> => {
  try {
    if (!isCapacitorAvailable()) {
      console.log('Not a native app, skipping Capacitor initialization');
      return;
    }

    console.log('Initializing Capacitor');
    
    // Android: Set status bar to be transparent with dark icons
    if (window.Capacitor.getPlatform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.setStyle({ style: Style.Dark });
    } 
    // iOS: Just use dark style for status bar
    else if (window.Capacitor.getPlatform() === 'ios') {
      await StatusBar.setStyle({ style: Style.Dark });
    }

    // Configure keyboard - iOS specific method
    if (window.Capacitor.getPlatform() === 'ios') {
      await Keyboard.setAccessoryBarVisible({ isVisible: false });
    }

    // Set up Android back button handler
    setupBackButton();
    
    // Hide splash screen immediately to effectively disable it
    try {
      await SplashScreen.hide();
      console.log('Splash screen hidden immediately');
    } catch (err) {
      console.error('Error hiding splash screen:', err);
    }
  } catch (error) {
    console.error('Error initializing Capacitor:', error);
  }
};

/**
 * Set up Android hardware back button handler
 */
export const setupBackButton = (): void => {
  if (!isCapacitorAvailable()) return;
  
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });
};